import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import { PagedModelMerchantResponse } from '@app/api/marketplace/models';
import { MeService, MerchantsService } from '@app/api/marketplace/services';
import { allowHal } from '@app/interceptors/hal-accept-interceptor';
import { AuthenticationActions } from '@authentication/store/authentication.slice';

import {
  ImpersonationActions,
  ImpersonationSelectors,
} from './impersonation.slice';

@Injectable({ providedIn: 'root' })
export class ImpersonationEffects {
  loadMerchants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.loadMerchants.trigger),
      withLatestFrom(
        this.store.select(ImpersonationSelectors.selectFilter),
        this.store.select(ImpersonationSelectors.selectSearchText)
      ),
      switchMap(([_action, filter, searchText]) =>
        this.merchantsService
          .listMerchants(
            {
              ...filter,
              companyNameLike: searchText ?? undefined,
            },
            allowHal()
          )
          .pipe(
            map((response: PagedModelMerchantResponse) =>
              ImpersonationActions.loadMerchants.success({
                response,
              })
            ),
            catchError(() => of(ImpersonationActions.loadMerchants.failure()))
          )
      )
    )
  );

  impersonateMerchant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.impersonateMerchant.trigger),
      switchMap((action) =>
        this.meService
          .impersonate({ body: { merchantId: action.merchantId } })
          .pipe(
            map(() => {
              this.store.dispatch(AuthenticationActions.refreshToken.trigger());
              return ImpersonationActions.impersonateMerchant.success();
            })
          )
      )
    )
  );

  restartImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.restartImpersonation.trigger),
      withLatestFrom(
        this.store.select(ImpersonationSelectors.selectImpersonatedMerchantId)
      ),
      switchMap(([_action, merchantId]) =>
        this.meService.impersonate({ body: { merchantId } }).pipe(
          map(() => {
            this.store.dispatch(AuthenticationActions.refreshToken.trigger());
            return ImpersonationActions.restartImpersonation.success();
          })
        )
      )
    )
  );

  removeImpersonation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationActions.removeImpersonation.trigger),
      switchMap((action) =>
        this.meService.removeImpersonation().pipe(
          map(() => {
            if (!action.noRefresh) {
              this.store.dispatch(AuthenticationActions.refreshToken.trigger());
            }
            return ImpersonationActions.removeImpersonation.success();
          })
        )
      )
    )
  );

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly merchantsService: MerchantsService,
    private readonly meService: MeService
  ) {}
}
